import * as Sentry from '@sentry/react';
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { ApiError } from '@/shared/types/api';
import { env } from '@/shared/config/env';
import { getEmbeddedOrgId, isEmbedded } from '@/shared/config/embedded-context';
import { clearSessionToken, getSessionToken } from '@/shared/services/session-token';

const DEFAULT_TIMEOUT = 30_000; // 30 seconds
export const UPLOAD_TIMEOUT = 300_000; // 5 minutes for file uploads

function generateCorrelationId(): string {
  if (
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.randomUUID === 'function'
  ) {
    return globalThis.crypto.randomUUID();
  }

  return `req-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

/**
 * Normalizes any Axios error into a structured ApiError.
 */
function normalizeApiError(error: AxiosError): ApiError {
  const response = error.response;
  if (response) {
    const data = response.data as Record<string, unknown> | undefined;
    return {
      status: response.status,
      message:
        (data?.['message'] as string) ||
        (data?.['error'] as string) ||
        error.message ||
        'An error occurred',
      code: (data?.['code'] as string) || undefined,
      details: (data?.['details'] as Record<string, unknown>) || undefined,
      timestamp: new Date().toISOString(),
    };
  }

  if (error.code === 'ECONNABORTED') {
    return {
      status: 408,
      message: 'Request timeout. Please try again.',
      code: 'TIMEOUT',
      timestamp: new Date().toISOString(),
    };
  }

  return {
    status: 0,
    message: error.message || 'Network error. Please check your connection.',
    code: 'NETWORK_ERROR',
    timestamp: new Date().toISOString(),
  };
}

function setupRequestInterceptors(instance: AxiosInstance): void {
  // Ensure credentials on every request
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    config.withCredentials = true;
    const sessionToken = getSessionToken();
    if (sessionToken) {
      config.headers.set('Authorization', `Bearer ${sessionToken}`);
    }
    // Embedded: tell the backend which Events.com org the host is showing, so
    // cookie auth selects/loads that org (and follows the host on org switch).
    const orgId = getEmbeddedOrgId();
    if (orgId) {
      config.headers.set('X-Organization-Id', orgId);
    }
    return config;
  });

  // Correlation ID for distributed tracing
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    config.headers.set('X-Correlation-Id', generateCorrelationId());
    return config;
  });

  // Sentry breadcrumb for outgoing requests
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    Sentry.addBreadcrumb({
      category: 'http',
      message: 'API Request',
      data: {
        url: config.url,
        method: config.method,
      },
      level: 'info',
    });
    return config;
  });
}

function setupResponseInterceptors(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (response) => {
      Sentry.addBreadcrumb({
        category: 'http',
        message: 'API Response',
        data: {
          url: response.config.url,
          method: response.config.method,
          status: response.status,
        },
        level: 'info',
      });
      return response;
    },
    async (error: AxiosError) => {
      const apiError = normalizeApiError(error);

      // Capture 5xx server errors in Sentry
      if (apiError.status >= 500) {
        Sentry.captureException(error);
      }

      if (apiError.status === 401) {
        // Only a 401 from the session endpoint means the Promote session itself
        // is invalid. Every other 401 (org select/create, data endpoints, the
        // events picker) must not tear down a valid or just-minted session.
        // Also skip when embedded (host cookie owns auth) or when no token
        // existed (a signed-out user's 401 just routes to login).
        const reqUrl = error.config?.url ?? '';
        const isSessionEndpoint = /\/auth\/session(\/|\?|$)/.test(reqUrl);
        if (!isSessionEndpoint || isEmbedded() || !getSessionToken()) {
          return Promise.reject(apiError);
        }
        // Lazy import to avoid circular dependency
        const { useUIStore } = await import('@/shared/stores/ui-store');
        const { useAuthStore } = await import('@/shared/stores/auth-store');
        clearSessionToken();
        useAuthStore.getState().logout();
        useUIStore.getState().addToast({
          message: 'Session expired. Please log in again.',
          variant: 'error',
        });
        window.location.href = '/login';
        return Promise.reject(apiError);
      }

      return Promise.reject(apiError);
    },
  );
}

function createApiClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: env.VITE_API_BASE_URL,
    withCredentials: true,
    timeout: DEFAULT_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  setupRequestInterceptors(instance);
  setupResponseInterceptors(instance);

  return instance;
}

/**
 * Singleton Axios instance. All API modules use this -- never create new instances.
 */
export const apiService = createApiClient();

/**
 * Upload configuration for multipart/form-data requests with longer timeout.
 */
export function uploadConfig(onProgress?: (pct: number) => void): AxiosRequestConfig {
  return {
    timeout: UPLOAD_TIMEOUT,
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress
      ? (e) => onProgress(Math.round((e.loaded / (e.total ?? 1)) * 100))
      : undefined,
  };
}
